import { SkillSubStates } from '../models/SkillState';

export const mapSkillStatus = (subState: string, score: number | null = null, maxScore?: number | null) => {
  // business logic applied following this documentation: https://newclassrooms.atlassian.net/wiki/spaces/KERMIT/pages/4489226/Skill+Card

  let inferredOrDiscrepancy = false;
  let applyRatingStars = true;
  if (
    subState === SkillSubStates.INFERRED_VIA_ASSESSMENT ||
    subState === SkillSubStates.INFERRED_VIA_DIAGNOSTIC ||
    subState === SkillSubStates.INFERRED_VIA_EXITSLIP ||
    subState === SkillSubStates.DISCREPANCY_ASSESSMENT_ASSESSMENT ||
    subState === SkillSubStates.DISCREPANCY_DIAGNOSTIC_ASSESSMENT ||
    subState === SkillSubStates.DISCREPANCY_DIAGNOSTIC_DIAGNOSTIC ||
    subState === SkillSubStates.DISCREPANCY_EXITSLIP_ASSESSMENT ||
    subState === SkillSubStates.DISCREPANCY_EXITSLIP_DIAGNOSTIC
  ) {
    inferredOrDiscrepancy = true;
    applyRatingStars = false;
  }

  const possibleStatus = (subState: string) => {
    if (subState === SkillSubStates.NOT_TESTED) {
      return 'Not Started';
    }
    if (subState === SkillSubStates.NOT_INFERRED_VIA_DIAGNOSTIC) {
      return 'Not Started';
    }
    if (subState === SkillSubStates.INFERRED_VIA_DIAGNOSTIC) {
      return 'Placed Out';
    }
    // PASSED_VIA_EXITSLIP means the student passed the exit slips for every lesson associated with the
    // skill, so it reads as 'Passed' like the inferred substates above — but it is direct evidence, not
    // an inference, which is why it is deliberately absent from the `inferredOrDiscrepancy` block and
    // keeps applyRatingStars. Unlike PASSED_VIA_ASSESSMENT it cannot reach 'Passed' through the score
    // branch below: there is no assessment behind it, so `score` is undefined and it would otherwise
    // fall through to 'Not Started' and stop counting as a completed skill.
    if (
      subState === SkillSubStates.INFERRED_VIA_ASSESSMENT ||
      subState === SkillSubStates.INFERRED_VIA_EXITSLIP ||
      subState === SkillSubStates.PASSED_VIA_EXITSLIP
    ) {
      return 'Passed';
    }
    if (
      subState === SkillSubStates.DISCREPANCY_DIAGNOSTIC_ASSESSMENT ||
      subState === SkillSubStates.DISCREPANCY_ASSESSMENT_ASSESSMENT ||
      subState === SkillSubStates.DISCREPANCY_DIAGNOSTIC_DIAGNOSTIC ||
      subState === SkillSubStates.DISCREPANCY_EXITSLIP_ASSESSMENT ||
      subState === SkillSubStates.DISCREPANCY_EXITSLIP_DIAGNOSTIC
    ) {
      return 'Review';
    }
  };

  const possibleClass = (subState: string) => {
    if (subState === SkillSubStates.NOT_TESTED) {
      return 'nc_not-started';
    }
    if (subState === SkillSubStates.NOT_INFERRED_VIA_DIAGNOSTIC) {
      return 'nc_not-started';
    }
    if (subState === SkillSubStates.INFERRED_VIA_DIAGNOSTIC) {
      return 'nc_placed-out';
    }
    if (
      subState === SkillSubStates.INFERRED_VIA_ASSESSMENT ||
      subState === SkillSubStates.INFERRED_VIA_EXITSLIP ||
      subState === SkillSubStates.PASSED_VIA_EXITSLIP
    ) {
      return 'nc_passed';
    }
    if (
      subState === SkillSubStates.DISCREPANCY_DIAGNOSTIC_ASSESSMENT ||
      subState === SkillSubStates.DISCREPANCY_ASSESSMENT_ASSESSMENT ||
      subState === SkillSubStates.DISCREPANCY_DIAGNOSTIC_DIAGNOSTIC ||
      subState === SkillSubStates.DISCREPANCY_EXITSLIP_ASSESSMENT ||
      subState === SkillSubStates.DISCREPANCY_EXITSLIP_DIAGNOSTIC
    ) {
      return 'nc_review';
    }
    return 'nc_not-started';
  };

  const possiblesScores: { [key: number]: string } = {
    0: 'Keep Learning',
    2: 'Keep Learning',
    4: 'Keep Learning',
    6: 'Almost There',
    8: 'Passed',
    10: 'Passed',
  };

  const possiblesClassScores: { [key: number]: string } = {
    0: 'nc_keep-learning',
    2: 'nc_keep-learning',
    4: 'nc_keep-learning',
    6: 'nc_almost-there',
    8: 'nc_passed',
    10: 'nc_perfect',
  };
  if (score !== null && score !== undefined && score >= 0 && !inferredOrDiscrepancy) {
    if (typeof maxScore === 'number' && maxScore > 0) {
      const pct = Math.round((score / maxScore) * 100);
      if (pct >= 80) {
        return { status: 'Passed', className: 'nc_passed', applyRatingStars };
      }
      if (pct >= 60) {
        return { status: 'Almost There', className: 'nc_almost-there', applyRatingStars };
      }
      if (pct > 0) {
        return { status: 'Keep Learning', className: 'nc_keep-learning', applyRatingStars };
      }
    }
    const isDiscrete = score <= 10 && score % 2 === 0;
    if (isDiscrete) {
      return {
        status: possiblesScores[score],
        className: possiblesClassScores[score],
        applyRatingStars: applyRatingStars,
      };
    }
  }

  if (possibleStatus(subState)) {
    return { status: possibleStatus(subState), className: possibleClass(subState), applyRatingStars: applyRatingStars };
  }

  return { status: 'Not Started', className: 'nc_not-started', applyRatingStars: applyRatingStars };
};
